import { BasketMarketOfferEntity } from '@genial/services';
import { Utils } from '@genial/utils';

import { formatAvailableAmount } from '../../product/normalizer';
import { messageError } from '../constants';

import { baseNormalization, normalizeCustomerBasketOffersData } from '.';

const getFixedIncomeBuyingDetails = (
  item: ReturnType<typeof normalizeCustomerBasketOffersData>[number]
) => {
  const getProfitability = () => {
    const profitabilitySpread = item.requestedOfferDetail?.profitabilityFormatted;
    return profitabilitySpread || item.offer.profitabilityFormatted || messageError;
  };

  const getMinimumApplication = () => {
    const minimumApplicationSpread = item.requestedOfferDetail?.applicationIncreaseAmount;
    if (minimumApplicationSpread) {
      return Utils.Mask.Money(minimumApplicationSpread);
    }
    return item.offer.applicationMinimumAmountFormatted || messageError;
  };

  const getEstimatedAtMaturity = () => {
    const value = item.requestedAmount;
    const profitabilityToMaturity =
      item.requestedOfferDetail?.profitabilityToMaturity || item.offer.profitabilityToMaturity || 0;
    const estimatedRentabilityValue = value * (profitabilityToMaturity / 100);
    const estimatedTaxValue = estimatedRentabilityValue * ((item.offer.incomeTaxAmount || 0) / 100);

    const estimated = value + (estimatedRentabilityValue - estimatedTaxValue);
    return estimated ? Utils.Mask.Money(estimated) : messageError;
  };

  return [
    { label: 'Rentabilidade', value: getProfitability() },
    { label: 'Estimado no vencimento', value: getEstimatedAtMaturity() },
    {
      label: 'Aplicação mínima',
      value: getMinimumApplication(),
    },
    {
      label: item.offer.riskRatingName ?? messageError,
      value: item.offer.riskRatingLevel ?? messageError,
      type: 'risk',
    },
    { label: 'Vencimento', value: item.offer.maturityDateFormatted },
  ];
};

const getFixedIncomeSellingDetails = (
  item: ReturnType<typeof normalizeCustomerBasketOffersData>[number]
) => {
  return [
    { label: 'Quantidade solicitada', value: formatAvailableAmount(item.requestedQuantity) },
    { label: 'IR', value: item.offer.ir ? Utils.Mask.Money(item.offer.ir) : messageError },
    {
      label: 'Previsão de liquidação*:',
      value: item?.offer?.redemptionDate
        ? Utils.Mask.DateFormat(item.offer.redemptionDate, 'dd/MM/yyyy')
        : messageError,
    },
  ];
};

export const normalizeCustomerBasketOffersFixedIncome = (data?: BasketMarketOfferEntity[]) => {
  if (!data) {
    return [];
  }

  const fixedIncomeData = data.find((item) => item.marketType === 'FixedIncome');

  if (!fixedIncomeData) {
    return [];
  }

  const fixedIncomeNormalizedData = normalizeCustomerBasketOffersData(
    fixedIncomeData?.basketOffers
  );

  return fixedIncomeNormalizedData?.map((item) => ({
    ...baseNormalization(item),
    type: fixedIncomeData.marketTypeTranslated || messageError,
    title: item.offer.productDisplay + (item?.offer?.issuerName || ''),
    productCategory: item.offer.productCategory || item.offer.category || 'Renda Fixa',
    details:
      item.operationType === 'buy'
        ? getFixedIncomeBuyingDetails(item)
        : getFixedIncomeSellingDetails(item),
    total: {
      label: item.operationType === 'buy' ? 'Valor da aplicação' : 'Valor do resgate',
      description: item.operationType === 'buy' ? '' : 'Valor liquido',
      value: item.requestedAmountFormatted,
    },
  }));
};
